import React, {
  memo,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react'
import { withStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { IfElse } from 'Components/IfElse'
import cn from 'classnames'

import Box from '@material-ui/core/Box'

import {
  GoogleMap,
  Circle,
  Polygon,
  DrawingManager,
} from '@react-google-maps/api'

import LoadingIndicator from '../LoadingIndicator/LoadingIndicator'

import CursorIcon from 'Assets/Images/icons/cursor-icon.js'
import RadiusIcon from 'Assets/Images/icons/radius-icon.js'
import PolygonIcon from 'Assets/Images/icons/polygon-icon.js'
import UndoIcon from 'Assets/Images/icons/undo-icon.js'
import RedoIcon from 'Assets/Images/icons/redo-icon.js'

import CursorIconActive from 'Assets/Images/icons/cursor-icon-active.js'
import RadiusIconActive from 'Assets/Images/icons/radius-icon-active.js'
import PolygonIconActive from 'Assets/Images/icons/polygon-icon-active.js'
import UndoActiveIcon from 'Assets/Images/icons/undo-active-icon.js'
import RedoActiveIcon from 'Assets/Images/icons/redo-active-icon.js'

import style from './style.js'

const MAP_TYPES = {
  CIRCLE: 'circle',
  POLYGON: 'polygon',
  D2D_POLYGON: 'd2d_polygon',
}

// Per-item memoized renderers. With thousands of polygons on a city plan,
// re-rendering every shape on every state change makes drawing/dragging
// crawl. React.memo with a custom equality keeps untouched shapes stable.
const itemPropsAreEqual = (a, b) => {
  if (a.item.id !== b.item.id) return false
  if (a.item.editMode !== b.item.editMode) return false
  if (a.item.areaType !== b.item.areaType) return false
  if (a.item.paths !== b.item.paths) return false
  if (a.item.center !== b.item.center) return false
  if (a.item.radius !== b.item.radius) return false
  return a.optionsKey === b.optionsKey
}

const buildOptionsKey = options => {
  if (!options) return ''
  return [
    options.fillColor,
    options.fillOpacity,
    options.clickable,
    options.editable,
    options.draggable,
    options.visible,
  ].join('|')
}

const MemoCircleItem = memo(
  ({
    item,
    refCircle,
    options,
    handleMapItemClick,
    handleCircleCenterChange,
    handleCircleRadiusChange,
  }) => (
    <Circle
      ref={ref => (refCircle.current[item.id] = ref)}
      center={{ lat: item.center.lat, lng: item.center.lng }}
      onClick={() => handleMapItemClick(item)}
      onCenterChanged={() => handleCircleCenterChange(item)}
      onRadiusChanged={() => handleCircleRadiusChange(item)}
      options={{
        ...options,
        strokeColor: '#000000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        radius: item.radius,
        zIndex: 1,
      }}
    />
  ),
  itemPropsAreEqual,
)
MemoCircleItem.displayName = 'MemoCircleItem'

const MemoPolygonItem = memo(
  ({ item, refPolygon, options, handleMapItemClick, handlePolygonResize }) => (
    <Polygon
      ref={ref => (refPolygon.current[item.id] = ref)}
      paths={item.paths}
      onClick={() => handleMapItemClick(item)}
      onMouseUp={() => handlePolygonResize(item)}
      options={{
        ...options,
        strokeColor: '#000000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        draggable: false,
        zIndex: 1,
      }}
    />
  ),
  itemPropsAreEqual,
)
MemoPolygonItem.displayName = 'MemoPolygonItem'

const MemoD2DPolygon = memo(
  ({ item, refPolygon }) => (
    <Polygon
      ref={ref => (refPolygon.current[item.id] = ref)}
      paths={item.paths}
      options={{
        strokeColor: '#000000',
        strokeOpacity: 0.3,
        strokeWeight: 1,
        draggable: false,
        fillOpacity: 0.2,
        zIndex: 1,
      }}
    />
  ),
  itemPropsAreEqual,
)
MemoD2DPolygon.displayName = 'MemoD2DPolygon'

// Approximate per-item bounding box used to skip rendering items
// completely outside the visible map viewport. Cached on the item
// itself via a non-enumerable property so we never recompute for the
// same shape (cities can have ~3k polygons; recomputing on every pan
// burnt the main thread).
const BBOX_KEY = '__mwtBBox'
const getItemBBox = item => {
  if (item[BBOX_KEY]) return item[BBOX_KEY]
  let bbox = null
  if (item.type === MAP_TYPES.CIRCLE && item.center) {
    // Convert radius (m) to ~degrees. 111_320 m per degree latitude;
    // longitude scaled by cos(lat). Slightly over-estimate so we never
    // cull a shape that's still partially visible at the edge.
    const radiusM = item.radius || 0
    const dLat = radiusM / 111320
    const dLng =
      radiusM /
      (111320 * Math.max(Math.cos((item.center.lat * Math.PI) / 180), 0.01))
    bbox = {
      south: item.center.lat - dLat,
      north: item.center.lat + dLat,
      west: item.center.lng - dLng,
      east: item.center.lng + dLng,
    }
  } else if (Array.isArray(item.paths) && item.paths.length > 0) {
    let south = Infinity
    let north = -Infinity
    let west = Infinity
    let east = -Infinity
    for (const p of item.paths) {
      const lat = p?.lat ?? p?.latitude
      const lng = p?.lng ?? p?.longitude
      if (typeof lat !== 'number' || typeof lng !== 'number') continue
      if (lat < south) south = lat
      if (lat > north) north = lat
      if (lng < west) west = lng
      if (lng > east) east = lng
    }
    if (south !== Infinity) bbox = { south, north, west, east }
  }
  if (bbox) {
    Object.defineProperty(item, BBOX_KEY, {
      value: bbox,
      enumerable: false,
      configurable: true,
    })
  }
  return bbox
}

const bboxIntersectsViewport = (bbox, viewport) => {
  if (!bbox || !viewport) return true
  if (bbox.north < viewport.south) return false
  if (bbox.south > viewport.north) return false
  if (bbox.east < viewport.west) return false
  if (bbox.west > viewport.east) return false
  return true
}

const MapWithToolbar = ({
  classes,
  label = '',
  showToolBar = false,
  circleOptions = {},
  polygonOptions = {},
  activeUndo = 0,
  activeRedo = 0,
  mapItemFillColor,
  mapItems = [],
  center = { lat: 0, lng: 0 },
  refCircle,
  refPolygon,
  handleUndo = () => {},
  handleRedo = () => {},
  handleDelete = () => {},
  removeEditMode = () => {},
  handleMapItemClick = () => {},
  onCircleComplete = () => {},
  onPolygonComplete = () => {},
  handleCircleCenterChange = () => {},
  handleCircleRadiusChange = () => {},
  handlePolygonResize = () => {},
  saving,
  savedPercentage,
  onMapInit = () => {},
  loading,
  polygonsInfo,
}) => {
  let mapRef = useRef(null)
  const googleMapRef = useRef(null)

  const [drawingManager, setDrawingManager] = useState(null)
  const [drawingMode, setDrawingMode] = useState(null)
  // Viewport bounds for off-screen polygon culling. Updated (throttled
  // via rAF) whenever the user pans / zooms the map. Until the first
  // bounds_changed fires, we render everything so the user never sees
  // an empty initial map.
  const [viewport, setViewport] = useState(null)
  const viewportRafRef = useRef(null)

  const handleKeyPress = useCallback(
    e => {
      if (
        (e.keyCode === 90 && e.metaKey && e.shiftKey) ||
        (e.keyCode === 89 && e.ctrlKey)
      ) {
        handleRedo()
      } else if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
        handleUndo()
      }

      if (e.keyCode === 8 || e.keyCode === 46) {
        handleDelete()
      }
    },
    [handleRedo, handleUndo, handleDelete],
  )

  const handleClickOutside = useCallback(
    event => {
      if (mapRef.current && !mapRef.current.contains(event.target)) {
        removeEditMode()
        setDrawingMode(null)
      }
    },
    [removeEditMode],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    window.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
      window.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleKeyPress, handleClickOutside])

  const onLoad = loadedDrawingManager => {
    setDrawingManager(loadedDrawingManager)
  }

  const handleCircleComplete = circle => {
    setDrawingMode(null)
    onCircleComplete(circle)
  }

  const handlePolygonComplete = polygon => {
    setDrawingMode(null)
    onPolygonComplete(polygon)
  }

  const renderMapItems = item => {
    switch (item.type) {
      case MAP_TYPES.CIRCLE: {
        const opts = circleOptions(item)
        return (
          <MemoCircleItem
            key={item.id}
            item={item}
            refCircle={refCircle}
            options={opts}
            optionsKey={buildOptionsKey(opts)}
            handleMapItemClick={handleMapItemClick}
            handleCircleCenterChange={handleCircleCenterChange}
            handleCircleRadiusChange={handleCircleRadiusChange}
          />
        )
      }
      case MAP_TYPES.POLYGON: {
        const opts = polygonOptions(item)
        return (
          <MemoPolygonItem
            key={item.id}
            item={item}
            refPolygon={refPolygon}
            options={opts}
            optionsKey={buildOptionsKey(opts)}
            handleMapItemClick={handleMapItemClick}
            handlePolygonResize={handlePolygonResize}
          />
        )
      }
      case MAP_TYPES.D2D_POLYGON:
        return (
          <MemoD2DPolygon
            key={item.id}
            item={item}
            refPolygon={refPolygon}
            optionsKey="d2d"
          />
        )

      default:
        return null
    }
  }

  const handleCursorBtnClick = () => {
    setDrawingMode(null)
    removeEditMode()
  }
  const handleRadiusBtnClick = () => {
    setDrawingMode(MAP_TYPES.CIRCLE)
    removeEditMode()
  }
  const handlePolygonBtnClick = () => {
    setDrawingMode(MAP_TYPES.POLYGON)
    removeEditMode()
  }

  useEffect(() => {
    if (drawingManager) {
      drawingManager.setDrawingMode(drawingMode)
    }
  }, [drawingMode, drawingManager])

  // Viewport culling: only render shapes whose bbox intersects the
  // current map viewport. Polygons being edited or selected always
  // render even if their bbox is technically outside the inflated
  // viewport, so editing controls never disappear under the user.
  // Shapes outside ~10k items remain unfiltered (very small maps don't
  // benefit and the filter would itself have a cost).
  const visibleMapItems = useMemo(() => {
    if (!viewport) return mapItems
    if (mapItems.length < 200) return mapItems
    return mapItems.filter(item => {
      if (item.editMode) return true
      const bbox = getItemBBox(item)
      return bboxIntersectsViewport(bbox, viewport)
    })
  }, [mapItems, viewport])

  return (
    <div
      style={{
        height: 700,
      }}
      className={classes.mapWrapper}
      ref={ref => (mapRef.current = ref)}
    >
      {saving && (
        <div className={classes.mapLoading}>
          <LoadingIndicator
            label="Saving distribution zones and intersected mission areas"
            color="inherit"
            percentage={savedPercentage}
          />
        </div>
      )}

      {loading ? (
        <div className={classes.mapLoading}>
          <LoadingIndicator
            percentage={
              polygonsInfo.totalCount
                ? (
                    (polygonsInfo.loaded / polygonsInfo.totalCount) *
                    100
                  ).toFixed(2)
                : 0
            }
            label={`Loading ${
              polygonsInfo.loaded && polygonsInfo.totalCount
                ? `${polygonsInfo.loaded}/${polygonsInfo.totalCount}`
                : ''
            }`}
            labelStyle={{ fontSize: '24px' }}
          />
        </div>
      ) : null}

      <GoogleMap
        onLoad={map => {
          googleMapRef.current = map
          onMapInit(map)
        }}
        onClick={removeEditMode}
        onBoundsChanged={() => {
          // Throttle to one update per animation frame; bounds_changed
          // fires dozens of times during a single pan and updating
          // state synchronously thrashes React with 2k+ polygons.
          if (viewportRafRef.current) return
          viewportRafRef.current = window.requestAnimationFrame(() => {
            viewportRafRef.current = null
            const map = googleMapRef.current
            if (!map) return
            const b = map.getBounds && map.getBounds()
            if (!b) return
            const ne = b.getNorthEast()
            const sw = b.getSouthWest()
            // Inflate by 25% so shapes just outside the visible area
            // appear instantly when the user pans (no pop-in).
            const latPad = (ne.lat() - sw.lat()) * 0.25
            const lngPad = (ne.lng() - sw.lng()) * 0.25
            setViewport({
              north: ne.lat() + latPad,
              south: sw.lat() - latPad,
              east: ne.lng() + lngPad,
              west: sw.lng() - lngPad,
            })
          })
        }}
        mapContainerStyle={{
          width: '100%',
          height: '100%',
          borderRadius: '5px 5px 0 0',
        }}
        center={center}
        zoom={11}
        onZoomChanged={() => {}}
        onCenterChanged={() => {}}
        options={{
          clickableIcons: false,
          zoomControl: true,
          disableDefaultUI: true,
          keyboardShortcuts: false,
        }}
      >
        {showToolBar ? (
          <Box>
            <div className={classes.mapHeader}>
              <Typography className={classes.zoneType}>{label}</Typography>
              <Box className={classes.mapControlsWrapper}>
                <IfElse condition={!drawingMode}>
                  <CursorIconActive />
                  <CursorIcon onClick={handleCursorBtnClick} />
                </IfElse>
                <IfElse condition={drawingMode === MAP_TYPES.CIRCLE}>
                  <RadiusIconActive />
                  <RadiusIcon onClick={handleRadiusBtnClick} />
                </IfElse>
                <IfElse condition={drawingMode === MAP_TYPES.POLYGON}>
                  <PolygonIconActive />
                  <PolygonIcon onClick={handlePolygonBtnClick} />
                </IfElse>
              </Box>
              <Box>
                <IfElse condition={activeUndo}>
                  <UndoActiveIcon
                    className={cn(classes.activeUndoRedo, classes.mr_8)}
                    onClick={handleUndo}
                  />
                  <UndoIcon className={classes.mr_8} />
                </IfElse>
                <IfElse condition={activeRedo}>
                  <RedoActiveIcon
                    className={classes.activeUndoRedo}
                    onClick={handleRedo}
                  />
                  <RedoIcon />
                </IfElse>
              </Box>
            </div>
            <DrawingManager
              onLoad={onLoad}
              onPolygonComplete={handlePolygonComplete}
              onCircleComplete={handleCircleComplete}
              drawingMode={drawingMode}
              options={{
                drawingControlOptions: { drawingModes: ['circle', 'polygon'] },
                drawingControl: false,
                polygonOptions: {
                  strokeColor: '#000000',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: mapItemFillColor,
                  fillOpacity: 1,
                  clickable: true,
                  draggable: false,
                  editable: true,
                  visible: true,
                  zIndex: 1,
                },
                circleOptions: {
                  strokeColor: '#000000',
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: mapItemFillColor,

                  fillOpacity: 1,
                  clickable: true,
                  draggable: true,
                  editable: true,
                  visible: true,
                  zIndex: 1,
                },
              }}
            />
          </Box>
        ) : null}

        {visibleMapItems.map(item => renderMapItems(item))}
      </GoogleMap>
    </div>
  )
}

export default withStyles(style)(MapWithToolbar)
